// --- Step 1: Update Imports ---
import React, { useEffect, useState, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Button, Dropdown, Badge, Spinner, ListGroup, Modal } from 'react-bootstrap';
import RMSidebar from '../../components/RMSidebar';
import { useAuth } from '../../hooks/useAuth';
import { notificationApi, searchApi } from '../../services/api';
import SearchResults from '../../components/SearchResults';
import './Dashboard.css';

// Import icons
import { FaSignOutAlt, FaUser, FaBell, FaQuestionCircle, FaSearch, FaTimes } from 'react-icons/fa';

const RMDashboard = () => {
  const navigate = useNavigate();
  const { logout, getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();

  // --- Step 2: Update State ---
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null); // Ref for dropdown container
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null); // Ref for search container

  // Set document title on mount
  useEffect(() => {
    document.title = 'RM Dashboard';
  }, []);

  // --- Step 3: Add Effects ---
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      // setLoadingNotifications(true); // Indicate loading only when dropdown is open or explicitly fetching list
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
        // Fetch full list only if dropdown is open, otherwise just count is fine for badge
        // This logic is moved to the second useEffect triggered by showNotifications
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      } finally {
         // setLoadingNotifications(false); // Loading handled in the other effect
      }
    };

    fetchNotificationsData(); // Initial fetch for count
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every 60 seconds

    return () => clearInterval(intervalId); // Cleanup interval
  }, []); // Run once for interval setup

  // Fetch full list specifically when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
        if (showNotifications) {
            setLoadingNotifications(true);
            try {
                const notificationsResponse = await notificationApi.getNotifications({ limit: 10 }); // Fetch latest 10
                setNotifications(notificationsResponse.data);
            } catch (error) {
                console.error('Error fetching notifications for dropdown:', error);
                // Handle error appropriately, maybe show a message in the dropdown
                setNotifications([]); // Clear notifications on error
            } finally {
                setLoadingNotifications(false);
            }
        }
    };
    fetchFullNotifications();
   }, [showNotifications]); // Trigger when dropdown visibility changes


  // Handle clicks outside the notification dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target) && !showSearchModal) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchModal]);
  
  // Handle search query changes with debounce
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If search query is empty, clear results
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    // Set a new timeout for search
    const timeout = setTimeout(() => {
      performSearch(searchQuery);
    }, 500); // 500ms debounce
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchQuery]);


  // --- Step 4: Update/Add Handlers ---
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Remove old handler:
  // const toggleNotificationDrawer = () => {
  //   setNotificationDrawerOpen(!notificationDrawerOpen);
  // };

  // Add new handlers:
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };

  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    // Only proceed if the notification exists and is unread
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      // Decrement count optimistically ONLY if it was unread
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Optional: Revert optimistic update or show error
    }
  };

  const handleMarkAllAsRead = async () => {
    // Only proceed if there are unread notifications
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      // setShowNotifications(false); // Optionally close dropdown
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      // Use more user-friendly formatting
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; } // Fallback
  };
  
  // Search function
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };
  
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery.trim().length > 1) {
      performSearch(searchQuery);
      // Show modal for mobile view
      if (window.innerWidth < 768) {
        setShowSearchModal(true);
      }
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };
  
  const handleSearchResultClick = () => {
    setShowSearchResults(false);
    setShowSearchModal(false);
  };


  return (
    <div className="rm-dashboard-layout">
      <RMSidebar />
      
      <div className="rm-content-wrapper">
        <header className="rm-header">
          <div className="rm-greeting">
            <span className="rm-greeting-text">Hi, {currentUser?.name || 'RM'} <span className="rm-greeting-emoji">👋</span></span>
          </div>


          
          <div ref={searchRef} className="rm-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search DSOs, invoices, payouts..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
              {isSearching && (
                <Spinner 
                  animation="border" 
                  size="sm" 
                  className="search-spinner" 
                />
              )}
            </div>
            
            {/* Search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                <SearchResults 
                  results={searchResults}
                  onResultClick={handleSearchResultClick}
                />
              </div>
            )}
          </div>

          <div className="rm-header-actions">
            <div className="dso-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            
            
            <div ref={notificationRef} className="position-relative rm-notifications me-3"> {/* Added me-3 for spacing */}
              <Button
                variant="link"
                className="text-secondary p-0 position-relative" // Use text-secondary or adjust color in CSS
                onClick={toggleNotifications}
                id="dropdown-notifications-rm"
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} /> {/* Adjusted size slightly */}
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light" // Ensure these classes are available (Bootstrap)
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }} // Fine-tune badge style
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              {/* Notification Dropdown Menu */}
              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown" // Add shadow and margin-top
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }} // Adjust size
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {/* Show loading spinner only when fetching the list for the dropdown */}
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read} // Apply action styles/behavior only if unread
                        onClick={() => handleMarkAsRead(notif.id)} // Handler checks if already read
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent', // Highlight unread
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none', // Unread indicator
                          padding: '0.75rem 1.25rem', // Adjust padding
                          borderBottom: '1px solid #eee' // Add subtle separator
                        }}
                        className="notification-item" // Add class for potential specific styling
                      >
                        {/* Adjust based on your actual notification object structure */}
                        {/* Assuming 'title' and 'message' based on Header.js example */}
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>
            {/* End of new notification dropdown */}


            <div className="rm-user-profile">
              <div className="rm-avatar">
                <FaUser />
              </div>
              <div className="rm-user-info">
                <span className="rm-user-name">
                  {currentUser?.name || 'RM User'}
                </span>
                <button className="rm-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* --- Step 5: Remove Notification Drawer --- */}
        {/* The notification drawer block has been removed */}

        <div className="rm-main-content">
          <Outlet />
        </div>
        
        {/* Search Modal for Mobile */}
        <Modal 
          show={showSearchModal} 
          onHide={() => setShowSearchModal(false)}
          className="search-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Search Results</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mobile-search-container mb-3">
              <div className="search-input-container">
                <FaSearch className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search DSOs, invoices, payouts..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyPress={handleSearchKeyPress}
                  autoFocus
                />
                {searchQuery && (
                  <button className="clear-search-btn" onClick={clearSearch}>
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
            
            {isSearching ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-3">Searching...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <SearchResults 
                results={searchResults} 
                onResultClick={handleSearchResultClick} 
              />
            ) : searchQuery.trim().length > 1 ? (
              <div className="text-center py-5">
                <p>No results found for "{searchQuery}".</p>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <FaSearch size={32} />
                <p className="mt-3">Enter at least 2 characters to search</p>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default RMDashboard;
